import React from 'react';
import { ContactConfig, SocialConfig, AppConfig } from '../types';
import { Phone, Mail, MapPin, Clock, Send, Instagram, Facebook, Youtube, CheckCircle2, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { EditableText } from './EditableText';
import { db, doc, setDoc } from '../lib/firebase';

interface ContactFormProps {
  contact: ContactConfig;
  social: SocialConfig;
  isVisualEditActive?: boolean;
  onUpdateConfig?: (partialConfig: Partial<AppConfig>) => void;
}

export function ContactForm({ contact, social, isVisualEditActive = false, onUpdateConfig }: ContactFormProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    location: '',
    serviceType: 'Duo Photo & Vidéo (Sur-Mesure)',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    const leadId = `lead-${Date.now()}`;
    const leadPayload = {
      id: leadId,
      createdAt: new Date().toISOString(),
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      date: formData.date,
      location: formData.location,
      serviceType: formData.serviceType,
      message: formData.message,
      status: 'Nouveau',
      sourceUrl: typeof window !== 'undefined' ? window.location.href : 'https://mariage.life-captured.fr'
    };

    try {
      // 1. Primary Save: Direct Cloud Firestore real-time sync
      await setDoc(doc(db, "contact_requests", leadId), leadPayload);
    } catch (fsErr) {
      console.warn("Direct Firestore save notice (will fallback to server API if needed):", fsErr);
    }

    try {
      // 2. Secondary Save: Server Endpoint for notification logging
      await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadPayload)
      });
    } catch (apiErr) {
      console.warn("Server API contact backup log:", apiErr);
    }

    // Success transition
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: '',
      location: '',
      serviceType: 'Duo Photo & Vidéo (Sur-Mesure)',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-24 bg-theme-bg border-t border-theme-border relative z-10">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="text-xs uppercase tracking-[0.25em] text-theme-accent font-mono block mb-3">
            {t('contact_intro_tag')}
          </span>
          <h2 className="text-3xl md:text-5xl font-mono text-theme-text font-extrabold tracking-tight mb-4">
            {t('contact_intro_title')}
          </h2>
          <p className="text-sm text-theme-secondary max-w-xl mx-auto font-light">
            {t('contact_intro_desc')}
          </p>
        </div>

        {/* Contact Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
          
          {/* Info Side Column */}
          <div className="lg:col-span-5 bg-theme-card border border-theme-border p-8 md:p-10 flex flex-col justify-between">
            <div className="space-y-8">
              <h3 className="text-xl font-serif text-theme-text font-semibold mb-6 flex items-center gap-2">
                LIFE-CAPTURED Studio
              </h3>

              {/* Contact Cards */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-theme-bg border border-theme-border text-theme-accent">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-1">
                      {t('contact_studio_label')}
                    </p>
                    <EditableText
                      value={contact?.address || ''}
                      onSave={(val) => onUpdateConfig?.({ contact: { ...contact, address: val } })}
                      isEditingMode={isVisualEditActive}
                      label="Adresse du Studio"
                    >
                      <p className="text-sm text-theme-text font-medium">{contact?.address}</p>
                    </EditableText>
                    <p className="text-sm text-theme-secondary">{contact?.zipCode} {contact?.city}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-theme-bg border border-theme-border text-theme-accent">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-1">
                      {t('contact_label_phone')}
                    </p>
                    <EditableText
                      value={contact?.phone || ''}
                      onSave={(val) => onUpdateConfig?.({ contact: { ...contact, phone: val } })}
                      isEditingMode={isVisualEditActive}
                      label="Téléphone de contact"
                    >
                      <a href={`tel:${contact?.phone}`} className="text-sm text-theme-text hover:text-theme-accent transition-colors">{contact?.phone}</a>
                    </EditableText>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-theme-bg border border-theme-border text-theme-accent">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-1">
                      {t('contact_label_email')}
                    </p>
                    <EditableText
                      value={contact?.email || ''}
                      onSave={(val) => onUpdateConfig?.({ contact: { ...contact, email: val } })}
                      isEditingMode={isVisualEditActive}
                      label="Email de contact"
                    >
                      <a href={`mailto:${contact?.email}`} className="text-sm text-theme-text hover:text-theme-accent transition-colors">{contact?.email}</a>
                    </EditableText>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-theme-bg border border-theme-border text-theme-accent">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-1">
                      {t('contact_hours_label')}
                    </p>
                    <p className="text-xs text-theme-secondary leading-relaxed">{contact.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social & Legal Bottom Links */}
            <div className="pt-8 border-t border-theme-border mt-8">
              <p className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-4">
                {t('contact_social_label')}
              </p>
              <div className="flex gap-4">
                {social.instagram && (
                  <a
                    href={social.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Profil Instagram de LIFE-CAPTURED Studio"
                    title="Instagram - LIFE-CAPTURED"
                    className="p-2 bg-theme-bg hover:bg-theme-accent/10 border border-theme-border hover:border-theme-accent/30 text-theme-secondary hover:text-theme-accent transition-all"
                  >
                    <Instagram className="w-4 h-4" />
                    <span className="sr-only">Instagram</span>
                  </a>
                )}
                {social.facebook && (
                  <a
                    href={social.facebook}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Page Facebook de LIFE-CAPTURED Studio"
                    title="Facebook - LIFE-CAPTURED"
                    className="p-2 bg-theme-bg hover:bg-theme-accent/10 border border-theme-border hover:border-theme-accent/30 text-theme-secondary hover:text-theme-accent transition-all"
                  >
                    <Facebook className="w-4 h-4" />
                    <span className="sr-only">Facebook</span>
                  </a>
                )}
                {social.youtube && (
                  <a
                    href={social.youtube}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Chaîne YouTube de LIFE-CAPTURED Studio"
                    title="YouTube - LIFE-CAPTURED"
                    className="p-2 bg-theme-bg hover:bg-theme-accent/10 border border-theme-border hover:border-theme-accent/30 text-theme-secondary hover:text-theme-accent transition-all"
                  >
                    <Youtube className="w-4 h-4" />
                    <span className="sr-only">YouTube</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Form Side Column */}
          <div className="lg:col-span-7 bg-theme-card border border-theme-border p-8 md:p-10">
            <AnimatePresence mode="sync">
              {!isSubmitted ? (
                <motion.form
                  key="contact-form"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact-name" className="block text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-2">
                        {t('contact_label_name')}
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        placeholder={t('contact_placeholder_name')}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-theme-bg border border-theme-border focus:border-theme-accent text-sm px-4 py-3 text-theme-text outline-none transition-colors rounded-none"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-2">
                        {t('contact_label_email')}
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        placeholder={t('contact_placeholder_email')}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-theme-bg border border-theme-border focus:border-theme-accent text-sm px-4 py-3 text-theme-text outline-none transition-colors rounded-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact-phone" className="block text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-2">
                        {t('contact_label_phone')}
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        placeholder={t('contact_placeholder_phone')}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-theme-bg border border-theme-border focus:border-theme-accent text-sm px-4 py-3 text-theme-text outline-none transition-colors rounded-none"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-date" className="block text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-2">
                        {t('contact_label_date')}
                      </label>
                      <input
                        id="contact-date"
                        type="date"
                        required
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full bg-theme-bg border border-theme-border focus:border-theme-accent text-sm px-4 py-3 text-theme-text outline-none transition-colors rounded-none font-mono text-theme-secondary"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-location" className="block text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-2">
                      {t('contact_label_location')}
                    </label>
                    <input
                      id="contact-location"
                      type="text"
                      placeholder={t('contact_placeholder_location')}
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full bg-theme-bg border border-theme-border focus:border-theme-accent text-sm px-4 py-3 text-theme-text outline-none transition-colors rounded-none"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-service" className="block text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-2">
                      {t('contact_label_service')}
                    </label>
                    <select
                      id="contact-service"
                      value={formData.serviceType}
                      onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                      className="w-full bg-theme-bg border border-theme-border focus:border-theme-accent text-sm px-4 py-3 text-theme-text outline-none transition-colors rounded-none font-mono"
                    >
                      <option value="Duo Photo & Vidéo (Yohann & Aurélie)">✨ Duo Complet Photo & Vidéo (Yohann & Aurélie — Offre Signature)</option>
                      <option value="Reportage Photo Solo (Aurélie)">📷 Reportage Photo Solo (Aurélie — Photo)</option>
                      <option value="Projet sur-mesure / Autre">💌 Autre projet / Devis sur-mesure</option>
                    </select>
                    <p className="text-[10px] text-theme-secondary mt-1 font-mono italic">
                      💡 La prestation vidéo est réalisée exclusivement par Yohann en formule Duo (avec Aurélie à la photo).
                    </p>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-[10px] font-mono uppercase tracking-widest text-theme-secondary mb-2">
                      {t('contact_label_message')}
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      rows={5}
                      placeholder={t('contact_placeholder_message')}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-theme-bg border border-theme-border focus:border-theme-accent text-sm px-4 py-3 text-theme-text outline-none transition-colors resize-none rounded-none"
                    />
                  </div>

                  {/* Submission Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-theme-accent disabled:bg-theme-card-hover disabled:text-theme-secondary text-theme-bg font-semibold text-xs uppercase tracking-[0.25em] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer shadow-md hover:opacity-90"
                    id="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-theme-bg border-t-transparent rounded-full animate-spin" />
                        <span>{t('contact_sending')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>{t('contact_btn_send')}</span>
                      </>
                    )}
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="success-message"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="h-full flex flex-col items-center justify-center text-center py-12"
                >
                  <div className="p-4 bg-theme-accent/10 border border-theme-accent/25 text-theme-accent rounded-full mb-6">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                  <h3 className="text-2xl font-serif text-theme-text font-bold mb-4">
                    {t('contact_success_title')}
                  </h3>
                  <p className="text-sm text-theme-secondary max-w-md leading-relaxed mb-8">
                    {t('contact_success_thanks')}
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="px-6 py-2.5 bg-theme-bg hover:bg-theme-card border border-theme-border text-xs text-theme-text uppercase tracking-wider font-mono transition-all cursor-pointer"
                  >
                    {t('contact_success_btn_another')}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>

      </div>
    </section>
  );
}
